import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { Check, Lightning } from "@phosphor-icons/react";

const FEATURES = [
  "Accès illimité aux opportunités de mission",
  "Recherche intelligente en langage naturel",
  "Filtres Clients finaux / ESN",
  "Contacts professionnels et outils de prospection",
  "Export CSV, modèles d'emails et messagerie SMTP/IMAP",
  "Historique et recherches sauvegardées",
  "Une seule offre, toutes les fonctionnalités incluses",
];

function fmtEUR(cents) {
  const v = (cents || 0) / 100;
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: Number.isInteger(v) ? 0 : 2, minimumFractionDigits: Number.isInteger(v) ? 0 : 2 }).format(v) + " €";
}

export default function PricingCards({ returnTo }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then(({ data }) => {
      const std = (data.plans || []).find((x) => x.plan === "standard") || (data.plans || [])[0] || null;
      setPlan(std);
    }).catch(() => {});
  }, []);

  const handleCheckout = async () => {
    trackEvent("select_plan", { plan: "standard", lookup_key: plan?.lookup_key, logged_in: !!user });
    if (!user) { navigate("/register"); return; }
    if (!plan) return;
    setLoading(true);
    try {
      try { await api.post("/events", { event: "checkout_started", props: { plan: plan.lookup_key, return_to: returnTo || null } }); } catch (_) { /* noop */ }
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key, origin_url: window.location.origin, return_to: returnTo || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const price = plan ? fmtEUR(plan.amount) : "29 €";

  return (
    <div data-testid="pricing-cards" className="max-w-lg">
      <div data-testid="pricing-plan-standard"
        className="relative rounded-2xl p-8 surface accent-glow border border-accent/40 flex flex-col">
        <span className="absolute -top-3 left-8 whitespace-nowrap bg-accent text-black text-xs font-mono font-bold px-3 py-1 rounded-full shadow-lg">
          Offre unique
        </span>

        <div className="flex items-baseline gap-2">
          <span className="font-display text-6xl font-extrabold text-fg" data-testid="price-monthly">{price}</span>
          <span className="text-muted text-lg font-semibold">HT / mois</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="price-flex-badges">
          <span className="text-xs font-semibold border border-accent/40 text-accent rounded-full px-2.5 py-1">Sans engagement</span>
          <span className="text-xs font-semibold border border-accent/40 text-accent rounded-full px-2.5 py-1">Résiliable à tout moment</span>
        </div>

        <p className="text-fg font-semibold mt-4 leading-snug" data-testid="pricing-anchor-line">
          Toutes les opportunités de mission. Un seul abonnement.
        </p>

        <ul className="space-y-2.5 my-7 flex-1">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-fg/90">
              <Check size={16} className="text-accent mt-0.5 shrink-0" weight="bold" /> {f}
            </li>
          ))}
        </ul>

        <button
          onClick={handleCheckout}
          disabled={loading || (!!user && !plan)}
          data-testid="checkout-btn-standard"
          className="w-full py-3.5 rounded-md font-semibold text-sm hover-lift flex items-center justify-center gap-2 bg-accent text-black hover:brightness-110 disabled:opacity-60"
        >
          {loading ? "Redirection…" : (<><Lightning size={15} weight="fill" /> S'abonner pour {price}/mois</>)}
        </button>
      </div>

      <p className="text-xs text-muted mt-4 max-w-lg" data-testid="pricing-legal">
        Paiement mensuel récurrent sécurisé par Stripe. Prix indiqué <span className="font-semibold">HT</span> ;
        TVA applicable en sus selon la réglementation en vigueur. Accès immédiat après paiement, aucun engagement :
        résiliez à tout moment, l'accès reste actif jusqu'à la fin du mois payé.
      </p>
    </div>
  );
}
